import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Truck, Phone, Clock, Navigation, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '16px'
};

// Must match the exact same array as LocationPicker — Google Maps Loader
// throws if it is called again with different options. ('directions' is NOT
// a valid library; DirectionsService is part of the core API.)
const libraries = ['places'];

const DriverTrackingMap = ({ orderId, deliveryLocation, deliveryCoordinates, language = 'pt' }) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Parse delivery coordinates
  const getDeliveryCoords = useCallback(() => {
    if (deliveryCoordinates) {
      const parts = deliveryCoordinates.split(',').map(p => parseFloat(p.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return { lat: parts[0], lng: parts[1] };
      }
    }
    return null;
  }, [deliveryCoordinates]);

  // Fetch driver location
  const fetchDriverLocation = useCallback(async () => {
    if (!orderId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}/driver-location`, {
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json();
        
        if (data.location.latitude && data.location.longitude) {
          setDriverLocation({
            lat: data.location.latitude,
            lng: data.location.longitude
          });
        }
        setDriverInfo(data.driver);
      }
    } catch (error) {
      console.error('Error fetching driver location:', error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Calculate route
  const calculateRoute = useCallback(() => {
    if (!isLoaded || !driverLocation || !window.google) return;
    
    const deliveryCoords = getDeliveryCoords();
    if (!deliveryCoords) return;

    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route(
      {
        origin: driverLocation,
        destination: deliveryCoords,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result);
          const route = result.routes[0].legs[0];
          setEta({
            duration: route.duration.text,
            distance: route.distance.text
          });
        }
      }
    );
  }, [isLoaded, driverLocation, getDeliveryCoords]);

  // Initial fetch and polling
  useEffect(() => {
    fetchDriverLocation();
    
    // Poll every 4 seconds for driver location (faster real-time fallback for WS)
    const interval = setInterval(fetchDriverLocation, 4000);
    
    // WebSocket connection for real-time updates
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/order_${orderId}`);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'driver_location') {
          setDriverLocation({
            lat: data.latitude,
            lng: data.longitude
          });
        }
      } catch (e) { /* ignore */ }
    };
    
    return () => {
      clearInterval(interval);
      try { ws.close(); } catch (e) { /* ignore */ }
    };
  }, [orderId, fetchDriverLocation]);

  // Calculate route when driver location changes
  useEffect(() => {
    calculateRoute();
  }, [driverLocation, calculateRoute]);

  if (loadError) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-4">
          <p className="text-red-500 text-sm">
            {language === 'pt' ? 'Erro ao carregar mapa' : 'Error loading map'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!isLoaded || loading) {
    return (
      <Skeleton className="h-[350px] rounded-2xl" />
    );
  }

  const deliveryCoords = getDeliveryCoords();
  const mapCenter = driverLocation || deliveryCoords || { lat: 38.4925, lng: -8.8975 };

  return (
    <Card className="rounded-2xl border-[#004E64]/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-[#004E64]">
          <Truck className="text-[#FF6B35]" size={20} />
          {language === 'pt' ? 'Tracking do Entregador' : 'Driver Tracking'}
          {driverLocation && (
            <Badge className="bg-green-100 text-green-700 ml-auto">
              <Navigation size={12} className="mr-1" />
              {language === 'pt' ? 'Ao vivo' : 'Live'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {/* Driver Info */}
        {driverInfo && (
          <div className="mb-4 p-3 bg-[#F4F1EA] rounded-xl">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3 min-w-0">
                {driverInfo.photo_url ? (
                  <img src={driverInfo.photo_url} alt={driverInfo.name} className="w-10 h-10 rounded-full object-cover shrink-0" data-testid="tracking-driver-photo" />
                ) : (
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-full flex items-center justify-center text-white font-bold shrink-0">
                    {driverInfo.name?.charAt(0) || 'D'}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-[#004E64] truncate">{driverInfo.name}</p>
                  {driverInfo.phone && (
                    <p className="text-xs text-[#9FA2B4]">{driverInfo.phone}</p>
                  )}
                </div>
              </div>
              {eta && (
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-[#FF6B35]">{eta.duration}</p>
                  <p className="text-xs text-[#9FA2B4]">{eta.distance}</p>
                </div>
              )}
            </div>
            {driverInfo.phone && (
              <a
                href={`tel:${driverInfo.phone}`}
                className="flex items-center justify-center gap-2 w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-semibold py-2 rounded-xl transition-colors"
                data-testid="call-driver-button"
              >
                <Phone size={16} />
                {language === 'pt' ? 'Ligar para o Entregador' : 'Call Driver'}
              </a>
            )}
          </div>
        )}

        {/* Map */}
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={14}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
          }}
        >
          {/* Driver Marker */}
          {driverLocation && (
            <Marker
              position={driverLocation}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#FF6B35" stroke="white" stroke-width="2">
                    <circle cx="12" cy="12" r="10" fill="#FF6B35"/>
                    <path d="M8 12h8M12 8v8" stroke="white" stroke-width="2"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20)
              }}
            />
          )}

          {/* Delivery Location Marker */}
          {deliveryCoords && (
            <Marker
              position={deliveryCoords}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#2EC4B6" stroke="white" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3" fill="white"></circle>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 40)
              }}
            />
          )}

          {/* Route */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#FF6B35',
                  strokeWeight: 4,
                  strokeOpacity: 0.8
                }
              }}
            />
          )}
        </GoogleMap>

        {/* Delivery Address */}
        <div className="mt-4 p-3 bg-[#2EC4B6]/10 rounded-xl">
          <p className="text-xs text-[#9FA2B4] mb-1">
            {language === 'pt' ? 'Entregar em:' : 'Deliver to:'}
          </p>
          <p className="text-sm text-[#004E64] flex items-center gap-2">
            <MapPin size={14} className="text-[#2EC4B6]" />
            {deliveryLocation}
          </p>
          {deliveryCoordinates && (
            <p className="text-xs text-[#2EC4B6] mt-1">📍 GPS: {deliveryCoordinates}</p>
          )}
        </div>

        {!driverLocation && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-xl text-center">
            <Clock className="mx-auto text-yellow-500 mb-2" size={24} />
            <p className="text-sm text-yellow-700">
              {language === 'pt' 
                ? 'A aguardar localização do entregador...' 
                : 'Waiting for driver location...'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverTrackingMap;
